import { createElement, HISTORY_CSS } from "./BURHistoryDom";
import { historyText } from "./BURHistoryTexts";

// A swipe is at least this far (px) and clearly more horizontal than vertical, so scrolling the page is not mistaken for it
const SWIPE_DISTANCE = 50;
const SWIPE_RATIO = 1.5;

export interface ICarouselSlide {
    // Built when the slide is first shown, so a long free spins session does not build every grid up front
    build: () => HTMLElement;
}

/**
 * Shows one slide at a time with previous / next buttons and a "2 / 12" counter, wrapping around at both ends
 * like the paytable pages.
 */
export default class BURHistoryCarousel {
    public readonly element: HTMLElement;

    private slides: ICarouselSlide[];
    private views: (HTMLElement | null)[];
    private body: HTMLElement;
    private counter: HTMLElement;
    private index = 0;
    private onChange?: () => void;

    /**
     * @param onChange called when another slide is shown, e.g. to stop what is playing in the one that is hidden
     * @param header shown above the arrows, in the same band (e.g. the page title)
     */
    constructor(
        slides: ICarouselSlide[],
        onChange?: () => void,
        header?: HTMLElement
    ) {
        this.slides = slides;
        this.views = slides.map(() => null);
        this.onChange = onChange;

        this.element = createElement("div", HISTORY_CSS.carousel);
        const top = createElement("div", HISTORY_CSS.carouselHeader);
        if (header) {
            top.appendChild(header);
        }
        this.element.appendChild(top);
        const nav = createElement("div", HISTORY_CSS.carouselNav);
        const btnPrev = this.createButton("◀", "previous", -1);
        const btnNext = this.createButton("▶", "next", 1);
        this.counter = createElement("span", HISTORY_CSS.carouselCounter);
        nav.appendChild(btnPrev);
        nav.appendChild(this.counter);
        nav.appendChild(btnNext);
        top.appendChild(nav);

        this.body = createElement("div", HISTORY_CSS.carouselBody);
        this.element.appendChild(this.body);
        this.addSwipe(this.body);
        this.show(0);
    }

    private createButton(
        text: string,
        title: "previous" | "next",
        step: number
    ): HTMLButtonElement {
        const button = document.createElement("button");
        button.setAttribute("type", "button");
        button.classList.add(HISTORY_CSS.stepperButton);
        button.textContent = text;
        button.title = historyText(title);
        button.onclick = () => this.step(step);
        return button;
    }

    // Wraps around at both ends
    private step(step: number): void {
        this.show(
            (this.index + step + this.slides.length) % this.slides.length
        );
    }

    // Swipe left for the next slide, right for the previous one. Vertical movement is left to the page to scroll.
    private addSwipe(target: HTMLElement): void {
        let startX = 0;
        let startY = 0;
        let tracking = false;
        target.addEventListener(
            "touchstart",
            (event) => {
                // A second finger (e.g. a pinch) is not a swipe
                tracking = event.touches.length === 1;
                if (tracking) {
                    startX = event.touches[0].clientX;
                    startY = event.touches[0].clientY;
                }
            },
            { passive: true }
        );
        target.addEventListener(
            "touchend",
            (event) => {
                if (!tracking) {
                    return;
                }
                tracking = false;
                const touch = event.changedTouches[0];
                const dx = touch.clientX - startX;
                const dy = touch.clientY - startY;
                if (
                    Math.abs(dx) >= SWIPE_DISTANCE &&
                    Math.abs(dx) > Math.abs(dy) * SWIPE_RATIO
                ) {
                    this.step(dx < 0 ? 1 : -1);
                }
            },
            { passive: true }
        );
        target.addEventListener("touchcancel", () => (tracking = false), {
            passive: true
        });
    }

    private show(index: number): void {
        const previous = this.views[this.index];
        if (previous) {
            previous.hidden = true;
        }
        this.index = index;
        let view = this.views[index];
        if (!view) {
            view = this.slides[index].build();
            this.views[index] = view;
            this.body.appendChild(view);
        }
        view.hidden = false;
        this.counter.textContent = `${index + 1} / ${this.slides.length}`;
        this.onChange?.();
    }
}
